/**
 * Centralized API Configuration
 * All API endpoints must be defined here - NO hardcoded URLs elsewhere
 * 
 * Structure: { module: { action: { endpoint, method } } }
 */

export const API_CONFIG = {
  // Authentication
  auth: {
    login: { endpoint: '/auth', method: 'POST' },
    logout: { endpoint: '/logout', method: 'POST' },
    getUserInfo: { endpoint: '/getUserInfo', method: 'GET' },
    getClient: { endpoint: '/getClient', method: 'GET' },
  },

  // HCP (Healthcare Provider)
  hcp: {
    search: { endpoint: '/solr/hcp', method: 'POST' },
    searchFacet: { endpoint: '/solr/hcp/getfacet', method: 'POST' },
    stateFacet: { endpoint: '/solr/hcp/getStateFacet', method: 'GET' },
    details: { endpoint: '/getHcpByMdmId/:mdmId', method: 'GET' },
    onekeySearch: { endpoint: '/onekeySearch', method: 'POST' },
    saveToMdm: { endpoint: '/saveHcpToMdm/:okIndvId', method: 'GET' },
    checkAvailability: { endpoint: '/checkHcpAvailability/:okIndvId', method: 'GET' },
    createNewAccount: { endpoint: '/createNewAccount/:okIndvId', method: 'POST' },
    updateRequest: { endpoint: '/hcp/updateRequest', method: 'PUT' },
  },

  // HCO (Healthcare Organization)
  hco: {
    search: { endpoint: '/solr/hco', method: 'POST' },
    searchFacet: { endpoint: '/solr/hco/getfacet', method: 'GET' },
    facilityFacet: { endpoint: '/solr/hco/getFacilityFacet', method: 'GET' },
    orgClassFacet: { endpoint: '/solr/hco/getOrgClassFacet', method: 'GET' },
    globalSearch: { endpoint: '/hcoGlobalSearch', method: 'POST' },
    details: { endpoint: '/getHcoByMdmId/:mdmId', method: 'GET' },
    saveToMdm: { endpoint: '/saveHcoToMdm/:okWorkplaceId', method: 'GET' },
  },

  // Drug / Product
  drug: {
    search: { endpoint: '/drugDcr/getAllDrugs', method: 'POST' },
    details: { endpoint: '/drugDcr/getDcrById/:batchId', method: 'GET' },
    brandMap: { endpoint: '/drugDcr/getAllBrands', method: 'GET' },
    facet: { endpoint: '/solr/drug/getDrugFacet', method: 'POST' },
    submitTypeChanges: { endpoint: '/drugSearch/submitTypeChanges', method: 'PUT' },
    submitChanges: { endpoint: '/drugSearch/submitChanges', method: 'PUT' },
  },

  // Inbox / Requests
  inbox: {
    getUserRequests: { endpoint: '/getAllOpenDcr', method: 'GET' },
    getSystemRequests: { endpoint: '/getAllOpenDcr', method: 'GET' },
    getHCPRequests: { endpoint: '/hcpDcr/getOpenBatches', method: 'GET' },
    getDrugRequests: { endpoint: '/drugDcr/getOpenDcr', method: 'GET' },
    getRequestDetails: { endpoint: '/getOpenDcr/:requestId', method: 'GET' },
    createRequest: { endpoint: '/createDcr/:requestId', method: 'POST' },
    approveRequest: { endpoint: '/closeDcr/:requestId', method: 'GET' },
    rejectRequest: { endpoint: '/closeDcr/:requestId', method: 'GET' },
    updateRequest: { endpoint: '/updateHcpRequest', method: 'PUT' },
    getStatusData: { endpoint: '/agent/agentic-workflow/status/:requestId', method: 'GET' },
    getAgentStatus: { endpoint: '/agent/status', method: 'GET' },
    getAgentWorkflowStatus: { endpoint: '/agent/agentic-workflow/status/:requestId', method: 'GET' },
  },

  // History
  history: {
    getRequestHistory: { endpoint: '/solr/dcr', method: 'POST' },
    getHCPHistory: { endpoint: '/solr/dcr', method: 'POST' },
    getDrugHistory: { endpoint: '/solr/dcr', method: 'POST' },
    getSystemHistory: { endpoint: '/hcpDcr/getClosedDcr', method: 'GET' },
    getActivityLog: { endpoint: '/getClosedDcrById/:requestId', method: 'GET' },
  },

  // Configuration (rules + workspace parity)
  config: {
    getWorkspace: { endpoint: '/rule/matchRule', method: 'GET' },
    saveWorkspace: { endpoint: '/rule/ruleConfig/matchRule/', method: 'POST' },
    getHcpModel: { endpoint: '/rule/matchRule', method: 'GET' },
    getDrugModel: { endpoint: '/rule/matchRule', method: 'GET' },
    getBusinessRules: { endpoint: '/rule/businessRule', method: 'GET' },
    updateBusinessRules: { endpoint: '/rule/ruleConfig/businessRule/', method: 'POST' },
    getMatchRules: { endpoint: '/rule/matchRule', method: 'GET' },
    updateMatchRules: { endpoint: '/rule/ruleConfig/matchRule/', method: 'POST' },
    getValidationRules: { endpoint: '/rule/matchRule', method: 'GET' },
    updateValidationRules: { endpoint: '/rule/ruleConfig/matchRule/', method: 'POST' },
    getCleansers: { endpoint: '/rule/matchRule', method: 'GET' },
    getReferenceDatasets: { endpoint: '/rule/matchRule', method: 'GET' },
    getUserGroups: { endpoint: '/rule/businessRule', method: 'GET' },
    updateUserGroups: { endpoint: '/rule/ruleConfig/businessRule/', method: 'POST' },
  },

  // DCR / Request
  dcr: {
    getAllOpen: { endpoint: '/getAllOpenDcr', method: 'GET' },
    search: { endpoint: '/solr/dcr', method: 'POST' },
    getOpenByRequestId: { endpoint: '/getOpenDcr/:requestId', method: 'GET' },
    getClosedByRequestId: { endpoint: '/getClosedDcrById/:requestId', method: 'GET' },
    autoMatch: { endpoint: '/autoMatch/:acctType', method: 'POST' },
    submitLlmFeedback: { endpoint: '/dcr/llm-feedback', method: 'POST' },
    getLlmFeedback: { endpoint: '/dcr/llm-feedback', method: 'GET' },
    getLlmFeedbackById: { endpoint: '/dcr/llm-feedback/:feedbackId', method: 'GET' },
    updateLlmFeedback: { endpoint: '/dcr/llm-feedback/:feedbackId', method: 'PUT' },
    deleteLlmFeedback: { endpoint: '/dcr/llm-feedback/:feedbackId', method: 'DELETE' },
    getFeedbackBySlotKey: { endpoint: '/dcr/feedback/:slotKey', method: 'GET' },
    closeDcr: { endpoint: '/closeDcr/:requestId', method: 'GET' },
    hcpMatch: { endpoint: '/hcpMatch/:onekeyId.:veevaId', method: 'GET' },
    onekeyVr: { endpoint: '/onekeyVr/:requestId', method: 'POST' },
    createDcr: { endpoint: '/createDcr/:requestId', method: 'POST' },
    getCardsDetails: { endpoint: '/dcr/details/cards', method: 'GET' },
    getPromptSlots: { endpoint: '/dcr/prompt-slots', method: 'GET' },
    getAgentPromptRules: { endpoint: '/dcr/agent-prompt-rules', method: 'GET' },
    getAgentPromptRuleById: { endpoint: '/dcr/agent-prompt-rules/:ruleId', method: 'GET' },
    updateAgentPromptRule: { endpoint: '/dcr/agent-prompt-rules/:ruleId', method: 'PUT' },
  },

  // HCP DCR workflow
  hcpDcr: {
    getOpenBatches: { endpoint: '/hcpDcr/getOpenBatches', method: 'GET' },
    getDcrById: { endpoint: '/hcpDcr/getDcrById/:batchId', method: 'GET' },
    closeBatch: { endpoint: '/close/batchId/:batchId', method: 'PUT' },
    getBatchRequest: { endpoint: '/hcpDcr/getBatch/:batchId/getRequest/:reqId', method: 'GET' },
    getTimelineEvents: { endpoint: '/hcpDcr/getTimeline/:batchId/:reqId', method: 'GET' },
    getClosedDcr: { endpoint: '/hcpDcr/getClosedDcr', method: 'GET' },
    updateHcpRequest: { endpoint: '/updateHcpRequest', method: 'PUT' },
    unmergeHcp: { endpoint: '/unmergeHcp/:mdmId/:source', method: 'PUT' },
    mergeHcp: { endpoint: '/mergeHcp/:mdmId', method: 'PUT' },
    closeRequest: { endpoint: '/closeRequest/:batchId/:requestId', method: 'PUT' },
    getMatchRules: { endpoint: '/rule/matchRule', method: 'GET' },
    createMatchRule: { endpoint: '/rule/ruleConfig/matchRule', method: 'POST' },
    createBusinessRule: { endpoint: '/rule/ruleConfig/businessRule', method: 'POST' },
    deleteMatchRule: { endpoint: '/rule/ruleConfig/matchRule/:groupName', method: 'DELETE' },
    deleteBusinessRule: { endpoint: '/rule/ruleConfig/businessRule/:groupName', method: 'DELETE' },
    getBusinessRules: { endpoint: '/rule/businessRule', method: 'GET' },
  },

  // Drug DCR workflow
  drugDcr: {
    getAll: { endpoint: '/drugDcr/getAllDcr', method: 'GET' },
    getOpen: { endpoint: '/drugDcr/getOpenDcr', method: 'GET' },
    getClosed: { endpoint: '/drugDcr/getClosedDcr', method: 'GET' },
    getById: { endpoint: '/drugDcr/getDcrById/:batchId', method: 'GET' },
    getAllBrands: { endpoint: '/drugDcr/getAllBrands', method: 'GET' },
    closeByBatchId: { endpoint: '/drugDcr/closeDcr/:batchId', method: 'POST' },
    updateDcr: { endpoint: '/drugDcr/updateDcr', method: 'PUT' },
  },

  // Matching / Import jobs / Dictionary
  matching: {
    addNewImportJobHco: { endpoint: '/addNewImportJob/:delimiter.:threshold.:status.:source.:accountType', method: 'POST' },
    addNewImportJobHcp: { endpoint: '/addNewImportJob/:delimiter.:threshold.:accountType', method: 'POST' },
    getAllImportJobs: { endpoint: '/getAllImportJobs/:jobType', method: 'GET' },
    getImportJobById: { endpoint: '/getImportJobById/:jobType/:jobId', method: 'GET' },
    updateJobDetails: { endpoint: '/updateJobDetails', method: 'POST' },
    getScores: { endpoint: '/getScores/:type', method: 'GET' },
    updateScores: { endpoint: '/updateScores/:type', method: 'POST' },
    getAllDictionarySynonyms: { endpoint: '/getAllDictionarySynonyms', method: 'GET' },
    getDictionaryWord: { endpoint: '/getDictionaryWord/:word', method: 'GET' },
    updateSynonym: { endpoint: '/updateSynonym/', method: 'POST' },
    createNewSynonym: { endpoint: '/createNewSynonym/', method: 'POST' },
    deleteSynonym: { endpoint: '/deleteSynonym/:word', method: 'DELETE' },
  },

  // Specialty / Market Baskets
  specialty: {
    getInclusions: { endpoint: '/api/specialtyInclusion', method: 'GET' },
    getAllSpecialties: { endpoint: '/specialtyInclusion/getAllSpcl', method: 'GET' },
    submitChanges: { endpoint: '/specialtyInclusion/submitChanges', method: 'POST' },
  },
  marketBaskets: {
    getMarkets: { endpoint: '/getMarkets', method: 'GET' },
    createMarket: { endpoint: '/createMarket', method: 'POST' },
    getBrands: { endpoint: '/getBrands', method: 'GET' },
    getMarketBasket: { endpoint: '/getMarketBasket/:market_id', method: 'GET' },
    updateMarketBasket: { endpoint: '/updateMarketBasket', method: 'PUT' },
    getFactors: { endpoint: '/getFactors/:market_id/:brand_id/:ndc', method: 'GET' },
  },

  // Legacy workflow parity endpoints used by React modules
  workflow: {
    getOpenDcrByRequestId: { endpoint: '/getOpenDcr/:reqId', method: 'GET' },
    getClosedDcrByRequestId: { endpoint: '/getClosedDcrById/:reqId', method: 'GET' },
    getHcpByMdmId: { endpoint: '/getHcpByMdmId/:mdmId', method: 'GET' },
    getHcoByMdmId: { endpoint: '/getHcoByMdmId/:mdmId', method: 'GET' },
    getHcpBatchById: { endpoint: '/hcpDcr/getDcrById/:batchId', method: 'GET' },
    getHcpBatchRequest: { endpoint: '/hcpDcr/getBatch/:batchId/getRequest/:reqId', method: 'GET' },
    getHcpTimelineEvents: { endpoint: '/hcpDcr/getTimeline/:batchId/:reqId', method: 'GET' },
    getPersistData: { endpoint: '/agent/agentic-workflow/persist/:requestId', method: 'GET' },
    getAgentStatus: { endpoint: '/agent/status', method: 'GET' },
    getDrugBatchById: { endpoint: '/drugDcr/getDcrById/:batchId', method: 'GET' },
    closeDrugDcrBatch: { endpoint: '/drugDcr/closeDcr/:batchId', method: 'POST' },
    closeHcpRequest: { endpoint: '/closeRequest/:batchId/:requestId', method: 'PUT' },
    generateEscalationEmail: { endpoint: '/dcr/escalation/generate', method: 'POST' },
    sendEscalationEmail: { endpoint: '/dcr/escalation/send', method: 'POST' },
  }
};

/**
 * Helper function to build endpoints with dynamic parameters
 * 
 * @param {string} template - Endpoint template with :param placeholders
 * @param {object} params - Key-value pairs for parameter replacement
 * @returns {string} - Resolved endpoint path
 * 
 * @example
 * buildEndpoint('/api/hcp/:id', { id: '12345' })
 * // Returns: '/api/hcp/12345'
 * 
 * buildEndpoint('/api/hcp/:id/source/:sourceId', { id: '123', sourceId: '456' })
 * // Returns: '/api/hcp/123/source/456'
 */
export const buildEndpoint = (template, params = {}) => {
  return template.replaceAll(/:(\w+)/g, (match, key) => {
    if (params[key] === undefined) {
      console.warn(`Missing parameter '${key}' for endpoint template: ${template}`);
      return match; // Keep placeholder if param missing
    }
    return params[key];
  });
};

/**
 * Get full API configuration for a specific module and action
 * 
 * @param {string} module - Module name (e.g., 'hcp', 'drug')
 * @param {string} action - Action name (e.g., 'search', 'details')
 * @returns {object} - API config object with endpoint and method
 * 
 * @example
 * getAPIConfig('hcp', 'search')
 * // Returns: { endpoint: '/api/hcp/search', method: 'POST' }
 */
export const getAPIConfig = (module, action) => {
  if (!API_CONFIG[module]) {
    throw new Error(`API module '${module}' not found in API_CONFIG`);
  }
  if (!API_CONFIG[module][action]) {
    throw new Error(`API action '${action}' not found in module '${module}'`);
  }
  return API_CONFIG[module][action];
};



